import React from 'react';
import { useLocation } from 'react-router-dom';
import type { Permission, PermissionOperationHint, PermissionResourceKey } from '@/access-control.config';
import { permissionResources } from '@/access-control.config';
import { useAuth } from '@/chassis/core/auth/hooks';
import { isAuthDisabled } from '@/chassis/core/auth/auth-config';
import type { PermissionOperationMap } from '@/chassis/core/auth/access-control/types';

export type ResourceAccessFlags = Partial<Record<PermissionOperationHint | 'manage', boolean>>;

export interface ResourceAccessProps {
    canRead?: boolean;
    canWrite?: boolean;
    canManage?: boolean;
    permissions?: ResourceAccessFlags;
}

interface ResourceAccessGateProps {
    resource: PermissionResourceKey;
    children: React.ReactElement;
}

export function ResourceAccessGate({ resource, children }: ResourceAccessGateProps) {
    const { hasPermission } = useAuth();
    const location = useLocation();
    const isViewRoute = /\/view(?:\/|$)/.test(location.pathname);
    const authDisabled = isAuthDisabled();
    const map: PermissionOperationMap<Permission> = permissionResources[resource] ?? {};

    const managePermission = map.manage;
    const writePermission = map.write;
    const readPermission = map.read;

    if (authDisabled) {
        if (!React.isValidElement(children)) {
            return <>{children}</>;
        }

        return React.cloneElement(children, {
            canRead: true,
            canWrite: isViewRoute ? false : true,
            canManage: isViewRoute ? false : true,
            permissions: {
                read: true,
                write: isViewRoute ? false : true,
                manage: isViewRoute ? false : true,
            },
        } as ResourceAccessProps);
    }

    const manageAllowed = managePermission ? hasPermission(managePermission) : false;
    const writeAllowed = writePermission ? hasPermission(writePermission) : false;
    const readAllowed = readPermission ? hasPermission(readPermission) : false;

    const canWrite = isViewRoute ? false : manageAllowed || writeAllowed;
    const canManage = isViewRoute ? false : manageAllowed;
    const canRead = canWrite || readAllowed;

    const permissions: ResourceAccessFlags = {
        read: readPermission ? readAllowed : undefined,
        write: writePermission ? (isViewRoute ? false : writeAllowed) : undefined,
        manage: managePermission ? (isViewRoute ? false : manageAllowed) : undefined,
    };

    if (!React.isValidElement(children)) {
        return <>{children}</>;
    }

    return React.cloneElement(children, {
        canRead,
        canWrite,
        canManage,
        permissions,
    } as ResourceAccessProps);
}

export function withResourceAccess(element: React.ReactElement, resource: PermissionResourceKey) {
    return <ResourceAccessGate resource={resource}>{element}</ResourceAccessGate>;
}
